import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ButtonModule } from 'primeng/button'
import { CheckboxModule } from 'primeng/checkbox'
import { DatePickerModule } from 'primeng/datepicker'
import { ProgressBarModule } from 'primeng/progressbar'
import { SkeletonModule } from 'primeng/skeleton'
import { TableModule } from 'primeng/table'
import { Subscription } from 'rxjs'
import { OutboundSyncsService } from '@/service/outbound-syncs.service'
import { RepaymentExport, RepaymentExportService } from '@/service/repayment-export.service'

@Component({
  selector: 'repayment-exports',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, DatePickerModule, ProgressBarModule, SkeletonModule, TableModule],
  templateUrl: './repayment-exports.component.html',
  styleUrl: './repayment-exports.component.scss',
})
export class RepaymentExportsComponent implements OnInit, OnDestroy {
  startDate: Date | null = null
  endDate: Date | null = null
  includeDeleted = false
  exports: RepaymentExport[] = []
  loading = true
  creating = false
  error: string | null = null
  downloadingId: string | null = null
  actionId: string | null = null
  private readonly subscriptions = new Subscription()
  private request: AbortController | null = null
  private destroyed = false
  private refreshTimer: ReturnType<typeof setInterval> | null = null

  constructor(
    readonly service: RepaymentExportService,
    private readonly liveUpdates: OutboundSyncsService,
  ) {}

  ngOnInit(): void {
    this.liveUpdates.startStatusMonitor()
    this.subscriptions.add(this.liveUpdates.repaymentExportsChanges$.subscribe(() => void this.refresh()))
    if (!this.service.isConfigured()) {
      this.loading = false
      return
    }
    void this.refresh()
    this.refreshTimer = setInterval(() => {
      if (this.exports.some((item) => item.status === 'QUEUED' || item.status === 'RUNNING')) void this.refresh()
    }, 60_000)
  }

  ngOnDestroy(): void {
    this.destroyed = true
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    this.request?.abort()
    this.subscriptions.unsubscribe()
  }

  async refresh(): Promise<void> {
    this.request?.abort()
    const request = this.request = new AbortController()
    this.loading = this.exports.length === 0
    this.error = null
    try {
      const exports = await this.service.list(request.signal)
      if (!this.destroyed && this.request === request) this.exports = exports
    } catch (error) {
      if (!this.destroyed && this.request === request && !(error instanceof DOMException && error.name === 'AbortError')) {
        this.error = error instanceof Error ? error.message : 'Failed to load repayment exports.'
      }
    } finally {
      if (!this.destroyed && this.request === request) this.loading = false
    }
  }

  async create(): Promise<void> {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.error = 'The start date must be on or before the end date.'
      return
    }
    this.creating = true
    this.error = null
    try {
      const created = await this.service.create({
        startDate: this.toIsoDate(this.startDate),
        endDate: this.toIsoDate(this.endDate),
        includeDeleted: this.includeDeleted,
      })
      this.exports = [created, ...this.exports.filter((item) => item.id !== created.id)]
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Failed to start repayment export.'
    } finally {
      this.creating = false
    }
  }

  async download(item: RepaymentExport): Promise<void> {
    this.downloadingId = item.id
    this.error = null
    try {
      const url = await this.service.download(item.id)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `repayments-export-${item.id}.csv`
      anchor.rel = 'noopener'
      anchor.click()
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Failed to download repayment export.'
    } finally {
      this.downloadingId = null
    }
  }

  async retry(item: RepaymentExport): Promise<void> {
    if (this.actionId) return
    this.actionId = item.id
    this.error = null
    try {
      const updated = await this.service.retry(item.id)
      this.exports = this.exports.map((row) => row.id === item.id ? updated : row)
      await this.refresh()
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Failed to retry export.'
    } finally { this.actionId = null }
  }

  async remove(item: RepaymentExport): Promise<void> {
    if (this.actionId || !window.confirm('Remove this failed export?')) return
    this.actionId = item.id
    this.error = null
    try {
      await this.service.remove(item.id)
      this.exports = this.exports.filter((row) => row.id !== item.id)
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Failed to remove export.'
    } finally { this.actionId = null }
  }

  range(item: RepaymentExport): string {
    const start = item.startDate ? this.dateOnly(item.startDate) : 'All time'
    const end = item.endDate ? this.dateOnly(item.endDate) : 'Today'
    return !item.startDate && !item.endDate ? 'All time' : `${start} – ${end}`
  }
  created(value: string): string { return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) }
  progress(item: RepaymentExport): number { return item.totalRows && item.totalRows > 0 ? Math.min(100, Math.round((item.completedRows / item.totalRows) * 100)) : 0 }
  progressText(item: RepaymentExport): string {
    if (item.status === 'FAILED') return 'Export failed'
    if (item.status === 'COMPLETE') return `${item.completedRows.toLocaleString()} rows exported`
    return item.totalRows === null ? 'Preparing export…' : `${item.completedRows.toLocaleString()} of ${item.totalRows.toLocaleString()} rows`
  }
  private toIsoDate(value: Date | null): string | null {
    if (!value) return null
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
  }
  private dateOnly(value: string): string { return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(new Date(`${value}T00:00:00`)) }
}
